import type { SalesPartnerStockData } from "~/features/salesPartnerStock/types";
import { Box, Button, CircularProgress, IconButton, Stack, styled, Typography } from "~/common/mui";
import { Dialog, DialogActions, DialogContent } from "~/common/mui/Dialog/Dialog";
import type { ProductData } from "~/domains/product/types/ProductData";
import { useSimpleLoader } from "~/features/selectionAssistant/housing/hooks/useSimpleLoader";
import { fetchSalesPartnerStock } from "~/features/salesPartnerStock/api/fetchSalesPartnerStock";
import { Card } from "~/features/configurator/Linear/components/Card";
import { SimpleStockIndicator } from "~/domains/product/components/StockIndicator/StockIndicator";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import { InfoCircle } from "~/common/components/InfoCircle/InfoCircle";
import { B2CCartProcessTile } from "~/features/cart/B2CCart/components/B2CCartProcessTile/B2CCartProcessTile";
import { Trans, useTranslation } from "react-i18next";
import { AddToCartService } from "~/features/addToCart/AddToCartService";
import { IconType } from "~/common/components/icon/IconType";
import { Icon } from "~/common/components/icon/Icon";
import { C_ON_TERTIARY_CONTAINER, C_TERTIARY_CONTAINER } from "~/common/components/icon/Colors";
import { salesStockDataSorter } from "~/features/salesPartnerStock/utils/salesPartnerStockHelper";
import useMediaQuery, { useIsMinTabletSize } from "~/common/hooks/useMediaQuery";
import { PopperTooltip } from "~/common/components/Tooltip";
import {
  trackAddToCartSalesPartnerPopUp,
  trackGoToShopSalesPartnerPopUp,
  trackSalesPartnerPopup,
  trackSalesPartnerPopUpClose,
} from "~/app/analytics/ga/ga";
import type { SalesPartnerStock } from "~/app/analytics/ga/salesPartner";
import type { GAPageTypeName } from "~/features/search/models/SearchPageType";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { MEDIA_QUERY_MAX_PHONE } from "~/common/utils/screenSizes";
import classNames from "classnames";

interface Props {
  onClose: () => void;
  product: ProductData;
  pageType: GAPageTypeName;
  quantity?: number;
}

const CenterContents = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "250px",
});

const B2CProcessTile = styled(B2CCartProcessTile)({
  paddingInline: "16px",
});

function mapSalesPartnerToGA(data: SalesPartnerStockData): SalesPartnerStock {
  return {
    id: data.affiliateID,
    name: data.name,
    availability: data.inStock ? "In Stock" : "Not In Stock",
  };
}

export const BuyFromSalesPartnerModal = ({ product, onClose, pageType, quantity = 1 }: Props) => {
  const { t } = useTranslation();

  const isMaxMobile = useMediaQuery(MEDIA_QUERY_MAX_PHONE);

  const { data, state } = useSimpleLoader(async () => {
    const data = await fetchSalesPartnerStock(product.code);
    data.sort(salesStockDataSorter);
    const parsedData = data.map(mapSalesPartnerToGA);
    trackSalesPartnerPopup(product.designation, product.sapMaterialNumber, pageType, parsedData);
    return data;
  }, [product.code]);
  const items = data ?? [];

  const showAddToCart = state === "loaded" && (!items.length || items.every((item) => !item.inStock));
  const handleAddToCart = () => {
    AddToCartService.openAddToCartWizard({
      productCode: product.code,
      restrictionQuery: "",
      initialData: {
        quantity,
      },
    });
    trackAddToCartSalesPartnerPopUp(product.designation, product.sapMaterialNumber, pageType);
    onClose();
  };
  const handleCloseDialog = () => {
    trackSalesPartnerPopUpClose(product.designation, product.sapMaterialNumber, pageType);
    onClose();
  };

  const makeTrackGoToSalesPartner = (salesPartner: SalesPartnerStockData) => {
    return () =>
      trackGoToShopSalesPartnerPopUp(
        product.designation,
        product.sapMaterialNumber,
        pageType,
        mapSalesPartnerToGA(salesPartner),
      );
  };

  const closeIcon = (
    <IconButton variant="contained" color={"secondary"} size={"tiny"} onClick={onClose}>
      <CloseRoundedIcon color="secondary" />
    </IconButton>
  );

  return (
    <Dialog onClose={handleCloseDialog} open maxWidth="desktop-m">
      <DialogContent>
        <Stack gap={2}>
          {state === "loading" && (
            <CenterContents>
              <CircularProgress />
            </CenterContents>
          )}
          {state === "error" && <CenterContents>{t("hybrisAjaxError")}</CenterContents>}
          {state === "loaded" && items.length === 0 && (
            <>
              <StyledDiv>
                <div>
                  <Trans
                    i18nKey="addToCart.salesPartner.noPartners"
                    components={{ paragraph: <Typography /> }}
                    values={{ designation: product.designation }}
                  />
                </div>
                {closeIcon}
              </StyledDiv>
              <B2CProcessTile />
            </>
          )}
          {state === "loaded" && items.length > 0 && (
            <>
              <StyledDiv>
                <Box sx={{ gap: 1, display: "flex", alignItems: isMaxMobile ? "flex-start" : "center" }}>
                  <Trans
                    parent={Typography}
                    i18nKey="addToCart.salesPartner.hasPartners"
                    values={{ designation: product.designation }}
                  />
                  <InfoCircle
                    className={classNames(isMaxMobile && "mt-1 mr-2")}
                    tooltipClassName={"p-1"}
                    content={t("addToCart.salesPartner.tooltip")}
                  />
                </Box>
                {closeIcon}
              </StyledDiv>
              <TileContainer>
                {items.map((i) => (
                  <SalesPartnerTile key={i.name} data={i} trackExternalClick={makeTrackGoToSalesPartner(i)} />
                ))}
              </TileContainer>
            </>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCloseDialog} variant={"text"}>
          {t("cancel")}
        </Button>
        {showAddToCart && (
          <Button onClick={handleAddToCart} startIcon={<ShoppingCartIcon />}>
            {t("addToCartPopup.add.submitBtn")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

const TileContainer = styled("div")(({ theme }) => ({
  display: "grid",
  gap: 16,
  "--columns": 1,
  [theme.breakpoints.up("tablet")]: {
    "--columns": 3,
  },
  [theme.breakpoints.up("desktop-l")]: {
    "--columns": 4,
  },
  gridTemplateColumns: "repeat(var(--columns, 1), minmax(0, 1fr))",
}));

const SalesPartnerTile = styled(
  ({
    data,
    className,
    trackExternalClick,
  }: {
    data: SalesPartnerStockData;
    className?: string;
    trackExternalClick: () => void;
  }) => {
    const { t } = useTranslation();
    const isMinTablet = useIsMinTabletSize();
    return (
      <Card className={className}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", width: "100%" }}>
          <img src={data.logoUrl} alt={""} />
          {data.preferred && <PreferredIndicator />}
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%", flex: 1 }}>
          <Typography sx={{ fontWeight: "bold" }}>{data.name}</Typography>
          <SimpleStockIndicator inStock={data.inStock} />
          <Button
            size="medium"
            href={data.url}
            target="_blank"
            startIcon={<OpenInNewIcon />}
            fullWidth={!isMinTablet}
            onClick={trackExternalClick}
            sx={{ marginTop: "auto" }}
          >
            {t("addToCart.salesPartner.goToPartner")}
          </Button>
        </Box>
      </Card>
    );
  },
)({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  padding: 16,
  gap: 16,

  img: {
    width: 120,
    height: 32,
    objectFit: "contain",
    objectPosition: "left",
  },
});

const PreferredIndicator = styled((props) => {
  const { t } = useTranslation();
  return (
    <PopperTooltip tooltipContent={t("addToCart.salesPartner.partnerTooltip")}>
      <div {...props}>
        <Icon type={IconType.Star} size={12} />
      </div>
    </PopperTooltip>
  );
})(({ theme }) => ({
  width: 28,
  height: 28,
  borderRadius: 4,
  backgroundColor: C_TERTIARY_CONTAINER,
  color: C_ON_TERTIARY_CONTAINER,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const StyledDiv = styled("div")({
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
});
